import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY_PREFIX = 'quoting-page-state-';
const SAVE_DEBOUNCE_MS = 400;

// Roughly half a second of frames to wait for restored content to lay out before giving
// up and scrolling as far as the page allows.
const MAX_SCROLL_RESTORE_FRAMES = 30;

export const PAGE_KEYS = {
  quotation: 'quotation',
  autoQuotation: 'auto-quotation',
  myTobPlans: 'my-tob-plans'
};

function getStorageKey(pageKey) {
  return STORAGE_KEY_PREFIX + pageKey;
}

// Ids travel as a string in the URL and as a number in a record, so compare them as
// strings.
function normalizeContextId(contextId) {
  return contextId === null || contextId === undefined || contextId === '' ? null : String(contextId);
}

function readStoredPageState(pageKey, version) {
  try {
    const stored = localStorage.getItem(getStorageKey(pageKey));

    if (!stored) return null;

    const parsed = JSON.parse(stored);

    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.version !== version) return null;
    if (!parsed.values || typeof parsed.values !== 'object') return null;

    return parsed;
  } catch (error) {
    console.error(`Failed to read the saved "${pageKey}" page state:`, error);
    return null;
  }
}

export function clearPageState(pageKey) {
  try {
    localStorage.removeItem(getStorageKey(pageKey));
  } catch (error) {
    console.error(`Failed to clear the saved "${pageKey}" page state:`, error);
  }
}

// Every page snapshot this device is holding, for when the session ends.
export function clearAllPageState() {
  Object.values(PAGE_KEYS).forEach(clearPageState);
}

// Patches a saved value directly into another page's snapshot without that page being
// mounted - e.g. Auto Quotation flagging the Quotation page's showRefreshNow so it
// prompts to refresh premiums next time it's opened. Preserves everything else already
// in the snapshot (including contextId), and starts a fresh one if there isn't one yet.
export function patchPageStateValues(pageKey, patch, version = 1) {
  try {
    const existing = readStoredPageState(pageKey, version);

    localStorage.setItem(getStorageKey(pageKey), JSON.stringify({
      version,
      contextId: existing?.contextId ?? null,
      values: { ...(existing?.values || {}), ...patch }
    }));
  } catch (error) {
    console.error(`Failed to patch the "${pageKey}" page state:`, error);
  }
}

/**
 * Keeps a page's working state in localStorage so leaving the page and coming back
 * (or reloading the tab) resumes where the user left off.
 *
 * Declare the state that should survive with `page.useState(name, initialValue)`
 * instead of `useState(initialValue)` - everything else (dialogs, loading flags,
 * fetched reference data) stays out of the snapshot on purpose.
 *
 * @param pageKey unique name of the page, used as the localStorage key.
 * @param version bump this whenever the persisted shape changes so older snapshots
 *        are dropped instead of restored into a page that can no longer read them.
 * @param contextId what the snapshot belongs to, typically the record id in the URL.
 *        Asking for a specific record only restores a snapshot of that same record, so
 *        the draft of one quotation is never restored on top of another one. Opening the
 *        page without a record ("continue what I was doing") restores whichever snapshot
 *        is there - page.reset() is how a user asks for a blank page.
 */
export function usePersistedPageState(pageKey, { version = 1, contextId = null } = {}) {
  const storeRef = useRef(null);

  if (storeRef.current === null) {
    const stored = readStoredPageState(pageKey, version);
    const requestedContextId = normalizeContextId(contextId);
    const isRestorable = stored !== null
      && (requestedContextId === null || normalizeContextId(stored.contextId) === requestedContextId);

    storeRef.current = {
      values: isRestorable ? stored.values : {},
      restored: isRestorable && Object.keys(stored.values).length > 0
    };
  }

  const store = storeRef.current;

  const entriesRef = useRef({});
  const contextIdRef = useRef(null);
  const timeoutRef = useRef(null);
  const isDisabledRef = useRef(false);
  const isResetPendingRef = useRef(false);

  contextIdRef.current = normalizeContextId(contextId);

  // Rendering again means the reset has been applied to every value, so saving the state
  // of this render can no longer bring back what was just cleared.
  isResetPendingRef.current = false;

  const save = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (isDisabledRef.current) return;

    // A reset that navigates away unmounts this page before it can render the cleared
    // values, and saving them on the way out would undo the reset.
    if (isResetPendingRef.current) return;

    const values = {};

    Object.keys(entriesRef.current).forEach(name => {
      const entry = entriesRef.current[name];
      const value = entry.getValue();

      values[name] = entry.serialize ? entry.serialize(value) : value;
    });

    try {
      localStorage.setItem(getStorageKey(pageKey), JSON.stringify({
        version,
        contextId: contextIdRef.current,
        values
      }));
    } catch (error) {
      // Most likely the storage quota. Drop this page's snapshot and stop saving for
      // the rest of the session so a large quotation can never break the page itself.
      console.error(`Failed to save the "${pageKey}" page state:`, error);

      isDisabledRef.current = true;
      clearPageState(pageKey);
    }
  }, [pageKey, version]);

  // No dependency array on purpose: every render re-arms the debounced save with the
  // values that were just rendered.
  useEffect(() => {
    if (isDisabledRef.current) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(save, SAVE_DEBOUNCE_MS);
  });

  useEffect(() => {
    window.addEventListener('pagehide', save);

    return () => {
      window.removeEventListener('pagehide', save);

      // Leaving the page is exactly when the pending debounced save still matters.
      save();
    };
  }, [save]);

  function register(name, getValue, serialize, reset) {
    entriesRef.current[name] = { getValue, serialize, reset };
  }

  function resolveInitialValue(initialValue) {
    return typeof initialValue === 'function' ? initialValue() : initialValue;
  }

  function getInitialValue(name, initialValue, deserialize) {
    if (Object.prototype.hasOwnProperty.call(store.values, name)) {
      const storedValue = store.values[name];

      return deserialize ? deserialize(storedValue) : storedValue;
    }

    return resolveInitialValue(initialValue);
  }

  // Drop-in replacement for useState. `serialize` / `deserialize` are only needed for
  // values JSON cannot represent on its own, such as a Set.
  function usePersistedState(name, initialValue, { serialize, deserialize } = {}) {
    const [value, setValue] = useState(() => getInitialValue(name, initialValue, deserialize));

    register(name, () => value, serialize, () => setValue(() => resolveInitialValue(initialValue)));

    return [value, setValue];
  }

  // Drop-in replacement for useRef. The value is read at save time, so mutating
  // `.current` without a re-render is still persisted.
  function usePersistedRef(name, initialValue, { serialize, deserialize } = {}) {
    const ref = useRef(undefined);

    if (ref.current === undefined) {
      ref.current = getInitialValue(name, initialValue, deserialize);
    }

    register(name, () => ref.current, serialize, () => {
      ref.current = resolveInitialValue(initialValue);
    });

    return ref;
  }

  // Remembers how far down the page the user was and puts them back there when the page
  // is restored. The window is what scrolls (see components/Main.js).
  function useScrollRestoration() {
    const scrollYRef = usePersistedRef('scrollY', 0);
    const isPositioningRef = useRef(true);

    useEffect(() => {
      function handleScroll() {
        // Until this page has been put where it belongs, the window is still showing the
        // position of the page that was open before it - which is not this page's.
        if (isPositioningRef.current) return;

        scrollYRef.current = window.scrollY;
      }

      window.addEventListener('scroll', handleScroll, { passive: true });

      return () => window.removeEventListener('scroll', handleScroll);
    }, [scrollYRef]);

    useEffect(() => {
      // Changing route leaves the window scrolled where the previous page was, so the
      // position is always set here: to what this page had, or to the top when it has
      // nothing saved. Otherwise a page would inherit - and then persist as its own - the
      // scroll position of the page the user came from.
      const targetY = store.restored ? scrollYRef.current : 0;

      let frame = null;
      let attempts = 0;

      // Restored content is laid out over the next few frames, so the page is not tall
      // enough to scroll down to yet.
      function positionPage() {
        const maxScrollY = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);

        if (maxScrollY >= targetY || attempts >= MAX_SCROLL_RESTORE_FRAMES) {
          window.scrollTo(0, Math.min(targetY, maxScrollY));
          isPositioningRef.current = false;
          return;
        }

        attempts += 1;
        frame = requestAnimationFrame(positionPage);
      }

      frame = requestAnimationFrame(positionPage);

      return () => {
        cancelAnimationFrame(frame);
        isPositioningRef.current = false;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
  }

  // Re-labels the snapshot with the record the state actually belongs to, which is not
  // always the one in the URL: a page that was reset back to a blank quotation must not
  // be restored later on top of the record it was reset from. Safe to call while
  // rendering - it only updates the label used by the next save.
  function setContextId(value) {
    contextIdRef.current = normalizeContextId(value);
  }

  // Puts every value declared through this store back to what it would be on a page that
  // was never used, and forgets the snapshot. This is what a "New Quotation" action does.
  function reset() {
    isResetPendingRef.current = true;

    clearPageState(pageKey);

    Object.keys(entriesRef.current).forEach(name => {
      entriesRef.current[name].reset();
    });
  }

  return {
    // true when this render started from a saved snapshot instead of fresh defaults.
    restored: store.restored,
    save,
    reset,
    setContextId,
    useState: usePersistedState,
    useRef: usePersistedRef,
    useScrollRestoration
  };
}
